import { useEffect } from "react";
import { Star, MapPin, Lock, ArrowRight } from "lucide-react";
import PageHeader from "../components/PageHeader";
import SectionHeading from "../components/SectionHeading";
import { Reveal } from "../components/Reveal";
import { useConsultation } from "../context/ConsultationContext";
import { TESTIMONIALS } from "../data";

// Privacy-protected feedback samples. Names/handles are intentionally blurred
// to protect client identities — represented as stylized message cards.
const BLURRED_FEEDBACK = [
  { snippet: "Honestly the best decision — everything about my profile feels so much more put together now.", stars: 5 },
  { snippet: "Super quick to respond and never pushy. They explained every step in plain language.", stars: 5 },
  { snippet: "My accounts finally feel organised and consistent. Can't recommend the audit enough.", stars: 5 },
  { snippet: "Professional and discreet throughout. Exactly the kind of guidance I was looking for.", stars: 5 },
];

export default function Testimonials() {
  const { openModal } = useConsultation();
  useEffect(() => {
    document.title = "Client Reviews — JOBLSX";
  }, []);

  return (
    <div>
      <PageHeader
        overline="Client Reviews"
        title="Trusted by creators & businesses."
        subtitle="Real, verified feedback from clients we've worked with. Some responses are anonymized and blurred to protect client privacy."
      />

      {/* Full reviews grid */}
      <section className="py-12 md:py-20">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {TESTIMONIALS.map((t, i) => (
              <Reveal key={i} delay={(i % 3) * 0.06}>
                <figure
                  className="flex h-full flex-col rounded-2xl border border-white/10 bg-white/[0.03] p-7 backdrop-blur-xl transition-colors duration-300 hover:border-blue-500/40"
                  data-testid={`testimonial-page-card-${i}`}
                >
                  <div className="flex gap-1">
                    {Array.from({ length: t.rating }).map((_, s) => (
                      <Star key={s} className="h-4 w-4 fill-blue-500 text-blue-500" />
                    ))}
                  </div>
                  <blockquote className="mt-5 flex-1 leading-relaxed text-slate-200">&ldquo;{t.text}&rdquo;</blockquote>
                  <figcaption className="mt-6 flex items-center gap-3 border-t border-white/10 pt-5">
                    <div className="flex h-11 w-11 items-center justify-center rounded-full bg-blue-600/15 font-display font-semibold text-blue-400">
                      {t.name.charAt(0)}
                    </div>
                    <div>
                      <p className="font-medium text-white">{t.name}</p>
                      <p className="flex items-center gap-1.5 text-sm text-slate-500">
                        {t.role}
                        {t.country && (
                          <>
                            <span className="text-slate-700">·</span>
                            <MapPin className="h-3 w-3" /> {t.country}
                          </>
                        )}
                      </p>
                    </div>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Blurred private feedback */}
      <section className="py-16 md:py-24 bg-[#0a0a0a]">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <SectionHeading
            overline="Privacy First"
            title="Private feedback, blurred for confidentiality."
            subtitle="Many clients share feedback privately. With their permission we display it here with identities blurred to protect their privacy."
            className="mb-12"
          />
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {BLURRED_FEEDBACK.map((f, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <div
                  className="relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] p-6"
                  data-testid={`blurred-feedback-${i}`}
                >
                  {/* mock chat header — blurred identity */}
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-500/40 to-slate-600/40 blur-[6px]" />
                    <div className="space-y-1.5">
                      <div className="h-3 w-24 rounded bg-white/20 blur-[4px]" />
                      <div className="h-2.5 w-16 rounded bg-white/10 blur-[4px]" />
                    </div>
                  </div>
                  {/* message bubble */}
                  <div className="mt-4 rounded-xl rounded-tl-sm bg-blue-600/10 p-4">
                    <div className="flex gap-1">
                      {Array.from({ length: f.stars }).map((_, s) => (
                        <Star key={s} className="h-3.5 w-3.5 fill-blue-500 text-blue-500" />
                      ))}
                    </div>
                    <p className="mt-2 text-sm leading-relaxed text-slate-300">&ldquo;{f.snippet}&rdquo;</p>
                  </div>
                  <div className="mt-4 flex items-center gap-1.5 text-xs text-slate-500">
                    <Lock className="h-3.5 w-3.5" /> Identity blurred for privacy
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.1}>
            <div className="mx-auto mt-16 max-w-3xl rounded-3xl border border-white/10 bg-white/[0.02] p-8 text-center md:p-12">
              <h3 className="font-display text-2xl font-medium tracking-tight">Ready to get results worth reviewing?</h3>
              <p className="mt-3 text-slate-400">Book a consultation and experience the JOBLSX process for yourself.</p>
              <button
                onClick={openModal}
                className="mx-auto mt-6 flex items-center justify-center gap-2 rounded-full bg-blue-600 px-8 py-4 font-medium text-white shadow-[0_0_24px_rgba(37,99,235,0.4)] transition-all duration-300 hover:bg-blue-700 hover:-translate-y-1"
                data-testid="testimonials-book-button"
              >
                Book Consultation <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
